import { checkDefinedOrThrow, ResourceNotFoundError } from '@meterup/common';
import { useGraphQL } from '@meterup/graphql';
import { useMemo } from 'react';

import type {
  BlockedClientsForNetworkQuery,
  ClientChannelUtilizationQueryQuery,
} from '../../../gql/graphql';
import type { NetworkClient } from '../../../hooks/networkClients/useNetworkClients';
import type { TimeSeries } from '../../../utils/chart_utils';
import { graphql } from '../../../gql';
import { PermissionType } from '../../../gql/graphql';
import { useNetworkClients } from '../../../hooks/networkClients/useNetworkClients';
import { useNetwork } from '../../../hooks/useNetworkFromPath';
import { NosFeature } from '../../../hooks/useNosFeatures';
import { useSearchParamsState } from '../../../providers/SearchParamsStateProvider';
import { addValueToTimeSeries } from '../../../utils/chart_utils';
import { getDuration, IntervalUnits, TimePeriod } from '../../dateTimeRangePicker/utils';
import useAccessLevelForUser, {
  useGetAccessLevelForUser,
} from '../../HasAccess/useAccessLevelForUser';

export const clientNameOrNull = (c: Pick<NetworkClient, 'alias' | 'clientName'>) =>
  (c.alias || (c.clientName !== '*' ? c.clientName : null)) ?? null;

export const blockClientsInNetworkMutation = graphql(`
  mutation blockClientsInNetwork(
    $networkUUID: UUID!
    $macAddresses: [MacAddress!]
    $ouis: [String!]
  ) {
    blockClientsInNetwork(networkUUID: $networkUUID, macAddresses: $macAddresses, ouis: $ouis) {
      uuid
      networkUUID
      macAddress
      oui
      createdAt
    }
  }
`);

export const unblockClientsInNetworkMutation = graphql(`
  mutation unblockClientsInNetwork(
    $networkUUID: UUID!
    $macAddresses: [MacAddress!]
    $ouis: [String!]
  ) {
    unblockClientsInNetwork(networkUUID: $networkUUID, macAddresses: $macAddresses, ouis: $ouis) {
      uuid
      networkUUID
      macAddress
      oui
      createdAt
    }
  }
`);

export const blockedClientsForNetworkQuery = graphql(`
  query blockedClientsForNetwork($networkUUID: UUID!) {
    blockedClientsForNetwork(networkUUID: $networkUUID) {
      uuid
      networkUUID
      macAddress
      oui
      createdAt
    }
  }
`);

export type BlockedClient = BlockedClientsForNetworkQuery['blockedClientsForNetwork'][number];

graphql(`
  fragment BaseMetricsResponseFields on MetricsResponse {
    metadata {
      minValue
      maxValue
    }
    values {
      timestamp
      value
    }
  }
`);

export const allClientMetricsByClientQuery = graphql(`
  query ClientMetricsTimeseriesResponse(
    $networkUUID: UUID!
    $macAddress: MacAddress!
    $filter: AllClientMetricsTimeseriesFilterInput!
  ) {
    allClientMetricsByClient(networkUUID: $networkUUID, macAddress: $macAddress, filter: $filter) {
      rxBytes {
        ...BaseMetricsResponseFields
      }
      txBytes {
        ...BaseMetricsResponseFields
      }
    }
  }
`);

export const wirelessClientMetricsByClientQuery = graphql(`
  query WirelessClientMetricsByClientQuery(
    $networkUUID: UUID!
    $macAddress: MacAddress!
    $filter: ClientMetricsTimeseriesFilterInput!
  ) {
    wirelessClientMetricsByClient(
      macAddress: $macAddress
      networkUUID: $networkUUID
      filter: $filter
    ) {
      metadata {
        signal {
          minValue
          maxValue
        }
        noise {
          minValue
          maxValue
        }
        txBytes {
          minValue
          maxValue
        }
        rxBytes {
          minValue
          maxValue
        }
      }
      values {
        timestamp
        signal
        noise
        txRate
        rxRate
        snr
        txBytes
        rxBytes
        txMulticastBytes
        rxMulticastBytes
        txUnicastBytes
        rxUnicastBytes
        rxSuccessRatio
        txSuccessRatio
        rxRetryRatio
        txRetryRatio
        clientCount
      }
    }
  }
`);

export const countCVEsQuery = graphql(`
  query countCVEsBySeverityForMACAddressesBulkQuery($input: [CVEsByNetworkMacAddressesInput!]!) {
    countCVEsBySeverityForMACAddressesBulk(input: $input) {
      networkUUID
      macAddress
      counts {
        baseSeverity
        count
      }
    }
  }
`);

export function useBulkCVESeverityCounts(networkUUID: string, macAddresses: string[]) {
  const cpeAccess = useAccessLevelForUser({
    operatorsSkipFeatureFlags: false,
    permissions: [PermissionType.PermNetworkDevicesRead, PermissionType.PermNetworkCpeRead],
    featureFlags: 'cpe-tab',
  });
  const { data } = useGraphQL(
    countCVEsQuery,
    {
      input: [
        {
          networkUUID,
          macAddresses,
        },
      ],
    },
    { enabled: !!cpeAccess, suspense: false },
  );

  return useMemo(() => {
    const map: Record<
      string,
      { CRITICAL: number; HIGH: number; MEDIUM: number; LOW: number; UNKNOWN: number }
    > = {};
    if (data?.countCVEsBySeverityForMACAddressesBulk) {
      for (const res of data.countCVEsBySeverityForMACAddressesBulk) {
        const mac = res.macAddress;
        map[mac] = { CRITICAL: 0, HIGH: 0, MEDIUM: 0, LOW: 0, UNKNOWN: 0 };
        for (const c of res.counts) {
          map[mac][c.baseSeverity] = c.count;
        }
      }
    }
    return map;
  }, [data]);
}

export type ClientChannelUtilizationQueryResult =
  NonNullable<ClientChannelUtilizationQueryQuery>['channelUtilizationByClient'][number];

export const ClientChannelUtilizationQuery = graphql(`
  query ClientChannelUtilizationQuery(
    $networkUUID: UUID!
    $macAddress: MacAddress!
    $filter: ChannelUtilizationTimeseriesFilterInput!
  ) {
    channelUtilizationByClient(
      networkUUID: $networkUUID
      macAddress: $macAddress
      filter: $filter
    ) {
      timestamp
      totalUtilization
    }
  }
`);

export function clientChannelUtilizationMetricsToSeries(
  values: ClientChannelUtilizationQueryResult[],
  step: number,
) {
  const series: TimeSeries = {
    series_id: 'Channel utilization',
    series_name: 'Channel utilization',
    data: [],
  };

  values.forEach((value, i) => {
    const currDate = new Date(value.timestamp);
    const prevDate = i > 0 ? new Date(values[i - 1].timestamp) : null;

    if (i === values.length - 1 && Date.now() - 5 * 60 * 1000 > currDate.getTime()) {
      // If the last element in the graph is older than 10 minutes, ad a null value for the current
      // time, so we see the graph up to the current time.
      addValueToTimeSeries(series, new Date(), null);
    }

    if (prevDate && currDate.getTime() - prevDate.getTime() > 1000 * step) {
      const midpoint = new Date((prevDate.getTime() + currDate.getTime()) / 2);
      addValueToTimeSeries(series, midpoint, null);
    }

    addValueToTimeSeries(series, currDate, value.totalUtilization);
  });

  return series;
}

export const upsertMACAddressAliasMutation = graphql(`
  mutation upsertMACAddressAlias($companySlug: String!, $macAddress: MacAddress!, $alias: String!) {
    upsertMACAddressAlias(companySlug: $companySlug, macAddress: $macAddress, alias: $alias) {
      alias
    }
  }
`);

export const rpcDisconnectClientMutation = graphql(`
  mutation rpcDisconnectClient($mac: String!, $serialNumber: String!, $ssid: String!) {
    rpcWosDisconnectClient(mac: $mac, serialNumber: $serialNumber, ssid: $ssid)
  }
`);

export function useClientByMAC(macAddress: string): NetworkClient | undefined {
  const network = useNetwork();
  const internalAccess = useAccessLevelForUser({ internal: true });
  const [excludeMeterHardware] = useSearchParamsState<boolean>('excludeMeterHardware');
  const defaultPeriod = internalAccess ? TimePeriod.ThirtyDays : TimePeriod.OneDay;
  const [currentTimePeriod] = useSearchParamsState<number>('timePeriod', defaultPeriod);
  const lookbackMinutes = useMemo(
    () => getDuration(currentTimePeriod, IntervalUnits.Minutes),
    [currentTimePeriod],
  );

  const clientHistory = useNetworkClients(network, {
    filter: { macAddress, lookbackMinutes, excludeMeterHardware },
  });

  return clientHistory[0];
}

export function useClientNamesByMACForAccessPoint({
  lookbackMinutes,
  serialNumber: apSerialNumber,
}: {
  lookbackMinutes?: number;
  serialNumber?: string;
} = {}) {
  const network = useNetwork();
  const allNetworkClients = useNetworkClients(network, {
    filter: {
      excludeMeterHardware: false,
      apSerialNumber,
      lookbackMinutes,
    },
  });

  const nameMap = useMemo(
    () =>
      Object.fromEntries(
        allNetworkClients.map((client) => [
          client.macAddress,
          clientNameOrNull(client) ?? client.macAddress,
        ]),
      ),
    [allNetworkClients],
  );

  return nameMap;
}

export function useBlockedClientsOrNull() {
  const network = useNetwork();
  const getAccessLevel = useGetAccessLevelForUser();
  const blocklistAccess = getAccessLevel({
    nosFeatures: [NosFeature.COS_CLIENT_BLOCKLIST],
    featureFlags: 'client-blocklist',
  });

  return useGraphQL(
    blockedClientsForNetworkQuery,
    {
      networkUUID: network.UUID,
    },
    {
      enabled: !!blocklistAccess,
    },
  ).data?.blockedClientsForNetwork;
}

export function useBlockedClients() {
  return checkDefinedOrThrow(
    useBlockedClientsOrNull(),
    new ResourceNotFoundError('No blocked clients found'),
  );
}
